import { Navigate } from "react-router-dom";
import { signIn, resetPassword } from "aws-amplify/auth";
import type {
  ResetPasswordInput,
  SignInInput,
  SignInOutput,
} from "aws-amplify/auth";
import { Authenticator } from "@aws-amplify/ui-react";
import { routes } from "../../router/routes";
import { strings } from "../../resources/strings";

const US_COUNTRY_CODE = "1";
const US_PHONE_NUMBER_LENGTH = 10;
const E164_PHONE_NUMBER_PATTERN = /^\+[1-9]\d{7,14}$/;

const isEmailUsername = (username: string): boolean => username.includes("@");

export const normalizeLoginUsername = (username: string): string => {
  const trimmedUsername = username.trim();

  if (isEmailUsername(trimmedUsername)) {
    return trimmedUsername;
  }

  const digits = trimmedUsername.replace(/\D/g, "");

  // Explicit international input: when the user types a leading "+", trust it as a
  // full E.164 number and pass it through unchanged. This correctly handles every
  // country code — including non-US NANP "+1" numbers (Canada, Jamaica, etc.),
  // which genuinely use "+1" and must keep their full national number rather than
  // being re-interpreted as a US number.
  if (trimmedUsername.startsWith("+")) {
    const e164Number = `+${digits}`;
    if (E164_PHONE_NUMBER_PATTERN.test(e164Number)) {
      return e164Number;
    }
    // Thrown validation text bypasses Amplify I18n, so it comes from the app
    // strings dictionary.
    throw new Error(strings.login.phoneValidation);
  }

  // No "+" prefix: treat a bare number as US/NANP and auto-apply the +1 country
  // code. Accept either 10 national digits, or 11 digits led by the US code.
  const nationalNumber =
    digits.length === US_PHONE_NUMBER_LENGTH + US_COUNTRY_CODE.length &&
    digits.startsWith(US_COUNTRY_CODE)
      ? digits.slice(US_COUNTRY_CODE.length)
      : digits;

  if (nationalNumber.length === US_PHONE_NUMBER_LENGTH) {
    return `+${US_COUNTRY_CODE}${nationalNumber}`;
  }

  throw new Error(strings.login.phoneValidation);
};

const formFields = {
  signIn: {
    username: {
      label: strings.login.usernameLabel,
      placeholder: strings.login.usernamePlaceholder,
      autocomplete: "username",
      type: "text",
      isRequired: true,
    },
  },
  forgotPassword: {
    username: {
      label: strings.login.usernameLabel,
      placeholder: strings.login.usernamePlaceholder,
      autocomplete: "username",
      type: "text",
      isRequired: true,
    },
  },
};

const services = {
  async handleSignIn(input: SignInInput): Promise<SignInOutput> {
    return signIn({
      ...input,
      username: normalizeLoginUsername(input.username),
    });
  },
  async handleForgotPassword(input: ResetPasswordInput) {
    return resetPassword({
      ...input,
      username: normalizeLoginUsername(input.username),
    });
  },
};

const Login = () => {
  return (
    <div className="flex h-screen items-center justify-center w-90 bg-primary">
      <Authenticator
        hideSignUp={true}
        formFields={formFields}
        loginMechanisms={["username"]}
        services={services}
      >
        {() => <Navigate to={routes.dashboard.path} />}
      </Authenticator>
    </div>
  );
};

export default Login;
